import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import VolumeSubCharts from './VolumeSubCharts';
import { buildVolumeMarkers } from '../utils/volumeChartData';
import './TradingDashboard.css';

// ── Indicator definitions ────────────────────────────────────────────────────
// Each indicator maps to a backend API group and a field within that response.
// MACD is removed from the overlay toggles — it now lives in its own sub-panel.
const INDICATOR_DEFS = [
  // SMA group — all come from /api/indicators/sma
  { key: 'sma10',  label: 'SMA 10',  group: 'sma',  field: 'sma10',  color: '#A15217', lineWidth: 1 },
  { key: 'sma21',  label: 'SMA 21',  group: 'sma',  field: 'sma21',  color: '#173CA1', lineWidth: 1 },
  { key: 'sma50',  label: 'SMA 50',  group: 'sma',  field: 'sma50',  color: '#8D17A1', lineWidth: 1 },
  { key: 'sma100', label: 'SMA 100', group: 'sma',  field: 'sma100', color: '#1791A1', lineWidth: 1 },
  { key: 'sma200', label: 'SMA 200', group: 'sma',  field: 'sma200', color: '#A12017', lineWidth: 2 },
  // EMA group — all come from /api/indicators/ema
  { key: 'ema9',   label: 'EMA 9',   group: 'ema',  field: 'ema9',   color: '#56A117', lineWidth: 1 },
  { key: 'ema12',  label: 'EMA 12',  group: 'ema',  field: 'ema12',  color: '#A19517', lineWidth: 1 },
  { key: 'ema26',  label: 'EMA 26',  group: 'ema',  field: 'ema26',  color: '#A11746', lineWidth: 1 },
];

// Initial checkbox state — all off by default
const INITIAL_ACTIVE = Object.fromEntries(INDICATOR_DEFS.map(d => [d.key, false]));

// ── Helper: deduplicate + sort candle/line data by YYYY-MM-DD ────────────────
function dedupeSort(arr, timeKey = 'time') {
  const seen = new Map();
  arr.forEach(r => seen.set(r[timeKey].slice(0, 10), r));
  return Array.from(seen.values()).sort((a, b) =>
    a[timeKey].slice(0, 10) < b[timeKey].slice(0, 10) ? -1 : 1
  );
}

// ── Component ────────────────────────────────────────────────────────────────
function TradingDashboard() {
  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const candleSeriesRef   = useRef(null);
  const seriesMarkersRef  = useRef(null);

  // indicator series refs: { sma10: ISeriesApi, ... }
  const indSeriesRef = useRef({});
  // raw indicator data cache per group: { sma: [...], ema: [...] }
  const indDataRef   = useRef({});

  // ── MACD sub-panel refs ────────────────────────────────────────────────────
  const macdContainerRef = useRef(null);
  const macdChartRef     = useRef(null);
  const macdSeriesRef    = useRef(null);
  const zeroSeriesRef    = useRef(null);

  const [pair, setPair]                     = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [active, setActive]                 = useState(INITIAL_ACTIVE);
  const [showMacd, setShowMacd]             = useState(false);
  const [macdLoading, setMacdLoading]       = useState(false);
  const [latestMacd, setLatestMacd]         = useState(null);
  const [showVolume, setShowVolume]         = useState(false);
  const [volumeLoading, setVolumeLoading]   = useState(false);
  const [signalBars, setSignalBars]         = useState([]);
  const [latestTrend, setLatestTrend]       = useState(null);
  const [latestObv, setLatestObv]           = useState(null);
  const [priceChart, setPriceChart]         = useState(null);
  const [chartReady, setChartReady]         = useState(false);

  // ── Create chart once on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#FFFBF3' },
        textColor: '#16244A',
      },
      grid: {
        vertLines: { color: '#EDE3CF' },
        horzLines: { color: '#EDE3CF' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true },
      width:  chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    // Candlestick series
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C',
      downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C',
      wickDownColor: '#C0571F',
    });

    // Pre-create all indicator line series (hidden by default)
    const seriesMap = {};
    INDICATOR_DEFS.forEach(def => {
      const s = chart.addSeries(LineSeries, {
        color:     def.color,
        lineWidth: def.lineWidth,
        visible:   false,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      seriesMap[def.key] = s;
    });

    chartRef.current        = chart;
    candleSeriesRef.current = candleSeries;
    seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
    indSeriesRef.current    = seriesMap;
    setPriceChart(chart);
    setChartReady(true);

    // Resize observer
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        chart.applyOptions({ width, height });
      }
    });
    ro.observe(chartContainerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current        = null;
      candleSeriesRef.current = null;
      seriesMarkersRef.current = null;
      indSeriesRef.current    = {};
      indDataRef.current      = {};
      setPriceChart(null);
      setChartReady(false);
    };
  }, []);

  // ── Create MACD sub-chart once on mount ────────────────────────────────────
  useEffect(() => {
    if (!macdContainerRef.current) return;

    const macdChart = createChart(macdContainerRef.current, {
      layout:          { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid:            { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair:       { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6', scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale:       { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false, visible: false },
      width:  macdContainerRef.current.clientWidth,
      height: macdContainerRef.current.clientHeight,
    });

    const macdSeries = macdChart.addSeries(LineSeries, {
      color: '#1F4E8C', lineWidth: 2,
      priceLineVisible: false, lastValueVisible: true, title: 'MACD',
    });

    const zeroSeries = macdChart.addSeries(LineSeries, {
      color: '#4A5672', lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '0',
    });

    macdChartRef.current  = macdChart;
    macdSeriesRef.current = macdSeries;
    zeroSeriesRef.current = zeroSeries;

    const ro = new ResizeObserver(entries => {
      for (const e of entries) macdChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
    });
    ro.observe(macdContainerRef.current);

    return () => {
      ro.disconnect();
      macdChart.remove();
      macdChartRef.current = macdSeriesRef.current = zeroSeriesRef.current = null;
    };
  }, []);

  // ── Sync time-scales between price chart and MACD sub-chart ────────────────
  useEffect(() => {
    const priceChart = chartRef.current;
    const macdChart  = macdChartRef.current;
    if (!priceChart || !macdChart) return;

    let syncP = false, syncM = false;

    const unsubP = () => priceChart.timeScale().unsubscribeVisibleLogicalRangeChange(onPriceRange);
    const unsubM = () => macdChart.timeScale().unsubscribeVisibleLogicalRangeChange(onMacdRange);

    function onPriceRange(range) {
      if (syncM || !range) return;
      syncP = true;
      macdChart.timeScale().setVisibleLogicalRange(range);
      syncP = false;
    }
    function onMacdRange(range) {
      if (syncP || !range) return;
      syncM = true;
      priceChart.timeScale().setVisibleLogicalRange(range);
      syncM = false;
    }

    priceChart.timeScale().subscribeVisibleLogicalRangeChange(onPriceRange);
    macdChart.timeScale().subscribeVisibleLogicalRangeChange(onMacdRange);

    return () => { unsubP(); unsubM(); };
  }, [chartRef.current, macdChartRef.current]); // re-run once both charts are mounted

  // ── Fetch available pairs on mount ─────────────────────────────────────────
  useEffect(() => {
    fetch('/api/config/pairs')
      .then(r => r.json())
      .then(data => {
        setAvailablePairs(data.pairs || []);
        setPair(data.default || (data.pairs && data.pairs[0]) || '');
      })
      .catch(() => setError('Failed to load trading pairs.'));
  }, []);

  // ── Fetch + apply indicator group data ────────────────────────────────────
  const applyIndicatorGroup = useCallback(async (group, pairName) => {
    // Use cached data if available for this pair
    const cacheKey = `${group}:${pairName}`;
    let rows = indDataRef.current[cacheKey];

    if (!rows) {
      const url = `/api/indicators/${group}?pair=${encodeURIComponent(pairName)}&days=0`;
      const json = await fetch(url).then(r => r.json());
      rows = dedupeSort(json.data || []);
      indDataRef.current[cacheKey] = rows;
    }

    // Push data into each series belonging to this group
    INDICATOR_DEFS
      .filter(def => def.group === group)
      .forEach(def => {
        const series = indSeriesRef.current[def.key];
        if (!series) return;
        const points = rows
          .filter(r => r[def.field] != null)
          .map(r => ({ time: r.time.slice(0, 10), value: parseFloat(r[def.field]) }));
        series.setData(points);
      });
  }, []);

  // ── Fetch OHLC + reset indicators when pair changes ───────────────────────
  useEffect(() => {
    if (!pair || !chartReady || !candleSeriesRef.current || !chartRef.current) return;

    setLoading(true);
    setError(null);

    // Clear indicator data cache for new pair
    indDataRef.current = {};

    // Reset all indicator series to empty + hidden
    INDICATOR_DEFS.forEach(def => {
      const s = indSeriesRef.current[def.key];
      if (s) {
        s.setData([]);
        s.applyOptions({ visible: false });
      }
    });
    setActive(INITIAL_ACTIVE);

    setSignalBars([]);
    setLatestTrend(null);
    setLatestObv(null);
    seriesMarkersRef.current?.setMarkers([]);

    let cancelled = false;

    fetch(`/api/ohlc-daily?pair=${encodeURIComponent(pair)}&days=0`)
      .then((r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((json) => {
        if (cancelled) return;

        const raw = (json.data || []).map(row => ({
          time:  row.time.slice(0, 10),
          open:  parseFloat(row.open),
          high:  parseFloat(row.high),
          low:   parseFloat(row.low),
          close: parseFloat(row.close),
        }));

        const seen = new Map();
        raw.forEach(c => seen.set(c.time, c));
        const candles = Array.from(seen.values()).sort((a, b) =>
          a.time < b.time ? -1 : 1
        );

        if (candles.length === 0) {
          candleSeriesRef.current?.setData([]);
          setError(
            'No daily OHLC data yet. Restart backend.py and wait for the background OHLC seed, or run with --run-data-insert.',
          );
          setLoading(false);
          return;
        }

        candleSeriesRef.current.setData(candles);

        // Default zoom: last 2 months (60 days back → today)
        const now   = new Date();
        const from  = new Date(now);
        from.setDate(from.getDate() - 60);
        const toStr   = now.toISOString().slice(0, 10);
        const fromStr = from.toISOString().slice(0, 10);
        try {
          chartRef.current.timeScale().setVisibleRange({ from: fromStr, to: toStr });
        } catch {
          chartRef.current.timeScale().fitContent();
        }

        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load chart data.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pair, chartReady]);

  // ── Fetch volume / OBV signals when pair changes ───────────────────────────
  useEffect(() => {
    if (!pair) return;

    setVolumeLoading(true);
    fetch(`/api/indicators/volume-signals?pair=${encodeURIComponent(pair)}&days=0`)
      .then((r) => r.json())
      .then((json) => {
        const bars = json.data || [];
        setSignalBars(bars);
        setLatestTrend(json.latest_trend || null);
        setLatestObv(json.latest_obv || null);
        seriesMarkersRef.current?.setMarkers(buildVolumeMarkers(bars));
        setVolumeLoading(false);
      })
      .catch(() => {
        setSignalBars([]);
        setLatestTrend(null);
        setLatestObv(null);
        seriesMarkersRef.current?.setMarkers([]);
        setVolumeLoading(false);
      });
  }, [pair]);

  // ── Handle checkbox toggle ─────────────────────────────────────────────────
  const handleToggle = useCallback(async (key) => {
    const def    = INDICATOR_DEFS.find(d => d.key === key);
    const series = indSeriesRef.current[key];
    if (!def || !series || !pair) return;

    const nowActive = !active[key];
    setActive(prev => ({ ...prev, [key]: nowActive }));

    if (nowActive) {
      // Fetch group data if not yet loaded, then show
      try {
        await applyIndicatorGroup(def.group, pair);
        series.applyOptions({ visible: true });
      } catch (e) {
        console.error(`Failed to load ${def.label}:`, e);
        setActive(prev => ({ ...prev, [key]: false }));
      }
    } else {
      series.applyOptions({ visible: false });
    }
  }, [active, pair, applyIndicatorGroup]);

  const loadMacd = useCallback((pairName) => {
    if (!pairName || !macdSeriesRef.current) return Promise.resolve();

    setMacdLoading(true);

    return fetch(
      `/api/indicators/macd?pair=${encodeURIComponent(pairName)}&days=0&warmup=false`,
    )
      .then((r) => r.json())
      .then((json) => {
        const rows = json.data || [];
        const macdData = rows
          .filter(r => r.macd_line != null)
          .map(r => ({ time: r.time.slice(0, 10), value: parseFloat(r.macd_line) }));

        const seen = new Map();
        macdData.forEach(p => seen.set(p.time, p));
        const sorted = Array.from(seen.values()).sort((a, b) => (a.time < b.time ? -1 : 1));

        if (macdSeriesRef.current) macdSeriesRef.current.setData(sorted);
        if (zeroSeriesRef.current && sorted.length > 0) {
          zeroSeriesRef.current.setData(sorted.map(p => ({ time: p.time, value: 0 })));
        }

        if (rows.length > 0) {
          const last = rows[rows.length - 1];
          setLatestMacd(last.macd_line != null ? parseFloat(last.macd_line) : null);
        } else {
          setLatestMacd(null);
        }
        setMacdLoading(false);
      })
      .catch(() => {
        setMacdLoading(false);
      });
  }, []);

  // After chart + pair are ready (same gate as OHLC), load MACD so series align
  useEffect(() => {
    if (!pair || !chartReady) return;

    loadMacd(pair);
  }, [pair, chartReady, loadMacd]);

  // Refresh MACD when the sub-panel is opened (picks up new daily bars)
  useEffect(() => {
    if (!pair || !chartReady || !showMacd) return;
    loadMacd(pair);
  }, [showMacd, pair, chartReady, loadMacd]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="trading-dashboard">
      {/* ── Top controls bar ── */}
      <div className="dashboard-controls">
        <label htmlFor="pair-select" className="pair-label">Pair</label>
        <select
          id="pair-select"
          value={pair}
          onChange={e => setPair(e.target.value)}
          className="pair-select"
          disabled={availablePairs.length === 0}
        >
          {availablePairs.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <span className="interval-badge">Daily</span>

        <div className="indicator-divider" />

        {/* ── Indicator checkboxes ── */}
        <div className="indicator-toggles">
          {INDICATOR_DEFS.map(def => (
            <label
              key={def.key}
              className={`ind-toggle ${active[def.key] ? 'ind-active' : ''}`}
              style={{ '--ind-color': def.color }}
            >
              <input
                type="checkbox"
                checked={active[def.key]}
                onChange={() => handleToggle(def.key)}
              />
              <span className="ind-dot" />
              {def.label}
            </label>
          ))}
        </div>

        <div className="indicator-divider" />

        {/* ── MACD sub-panel toggle ── */}
        <button
          className={`macd-toggle-btn${showMacd ? ' active' : ''}`}
          onClick={() => setShowMacd(v => !v)}
        >
          <span className="macd-toggle-dot" />
          MACD
          {latestMacd != null && (
            <span className={`macd-toggle-value${latestMacd >= 0 ? ' bull' : ' bear'}`}>
              {latestMacd.toFixed(2)}
            </span>
          )}
          {macdLoading && <span className="macd-toggle-spinner" />}
        </button>

        <button
          type="button"
          className={`volume-panel-toggle${showVolume ? ' active' : ''}`}
          onClick={() => setShowVolume((v) => !v)}
        >
          <span className="volume-panel-toggle-dot" />
          Volume / OBV
          {volumeLoading && <span className="macd-toggle-spinner" />}
        </button>
      </div>

      {/* ── Charts area ── */}
      <div className="td-charts-area">
        {loading && (
          <div className="chart-overlay">
            <div className="spinner" />
            <span>Loading chart…</span>
          </div>
        )}
        {error && (
          <div className="chart-overlay error">
            <span>⚠️ {error}</span>
          </div>
        )}

        {/* Price + MA chart */}
        <div
          className={`td-price-wrapper${showMacd ? ' with-macd' : ''}${
            showVolume ? ' with-volume' : ''
          }${showMacd && showVolume ? ' with-both-panels' : ''}`}
        >
          <div ref={chartContainerRef} className="chart-container" />
        </div>

        <VolumeSubCharts
          visible={showVolume}
          loading={volumeLoading}
          signalBars={signalBars}
          latestTrend={latestTrend}
          latestObv={latestObv}
          parentChart={priceChart}
        />

        {/* MACD sub-panel */}
        <div className={`td-macd-wrapper${showMacd ? ' visible' : ''}${showVolume ? ' with-volume-visible' : ''}`}>
          <div className="td-macd-label">
            MACD (EMA 12 − EMA 26)
            {latestMacd != null && (
              <span className={`td-macd-value${latestMacd >= 0 ? ' bull' : ' bear'}`}>
                &nbsp;{latestMacd.toFixed(2)}
              </span>
            )}
          </div>
          <div ref={macdContainerRef} className="td-macd-container" />
        </div>
      </div>
    </div>
  );
}

export default TradingDashboard;
